import { remoteConfigs } from '@/constants/utils'
import { z } from 'zod'
import { Value } from 'firebase/remote-config'

type RemoteFlag<K extends string, T> = {
  key: K
  validOrDefault: (value: unknown) => T
  defaultValue: T
}

type RemoteFlagList = typeof remoteConfigs

type RemoteConfigMap = {
  [F in RemoteFlagList[number] as F['key']]: ReturnType<F['validOrDefault']>
}

export type PartialRemoteConfigMap = Partial<RemoteConfigMap>
export type RemoteConfigKey = (typeof remoteConfigs)[number]['key']

// Does type inference for the default values
export function setConfig<K extends keyof RemoteConfigMap>(
  target: PartialRemoteConfigMap,
  key: K,
  value: RemoteConfigMap[K]
) {
  target[key] = value
}

// function isPrimitive(val: unknown): val is string | number | boolean {
//   return (
//     typeof val === 'string' ||
//     typeof val === 'number' ||
//     typeof val === 'boolean'
//   );
// }

export const remoteConfigDefault = Object.fromEntries(
  remoteConfigs.map((flag) => {
    const value = flag.defaultValue
    return [flag.key, value]
  })
) as {
  [F in (typeof remoteConfigs)[number] as F['key']]: F['defaultValue']
}

// Firebase gives us objects as strings
// So for our defaults we need to stringify them
export const remoteConfigDefaultsForFirebase = Object.fromEntries(
  remoteConfigs.map((flag) => {
    const value = flag.defaultValue

    return [flag.key, typeof value === 'object' ? JSON.stringify(value) : value]
  })
) as unknown as {
  [F in (typeof remoteConfigs)[number] as F['key']]: F['defaultValue'] extends string | number | boolean
    ? F['defaultValue']
    : string
}

export function RemoteFlag<const K extends string, S extends z.ZodTypeAny>(
  key: K,
  schema: S,
  defaultValue: z.infer<S>
): RemoteFlag<K, z.infer<S>> {
  return {
    key,
    defaultValue,
    validOrDefault: (value: unknown): z.infer<S> => {
      const parsed = schema.safeParse(value)
      if (parsed.success) {
        return parsed.data
      }

      // console.warn(`[RemoteFlag] Invalid value for "${key}". Falling back to default.`, parsed.error.format())
      return defaultValue
    },
  }
}

// Firebase returns values in an unusable way
// We need to parse them to get the correct type
export function parseRemoteValue(value: Value, configDefinition: { defaultValue: any }) {
  switch (typeof configDefinition.defaultValue) {
    case 'boolean':
      return value.asBoolean()
    case 'string':
      return value.asString()
    case 'number':
      return value.asNumber()
    case 'object':
      try {
        return JSON.parse(value.asString())
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        return new Error(`Failed to parse JSON from remote config: ${value.asString()}`)
      }

    default:
      console.warn(`Unknown config type: ${configDefinition}`)
      return new Error(`Unknown config type: ${configDefinition}`)
  }
}
